export const AUTH_LANGUAGE_OVERRIDE_KEY = 'authLanguageOverride';
export const I18NEXT_LANGUAGE_KEY = 'i18nextLng';

const LANGUAGE_ALIASES = {
  zh: 'cn',
  'zh-cn': 'cn',
  'zh-hans': 'cn',
  'zh-hans-cn': 'cn',
  cn: 'cn'
};

const LANGUAGE_CODE_PATTERN = /^[a-z]{2,5}$/;

const parseStorageJson = (key) => {
  try {
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.warn(`Failed to parse ${key} language cache`, error);
    return null;
  }
};

export const normalizeLanguageCode = (language) => {
  if (typeof language !== 'string') return null;

  const normalized = language.trim().toLowerCase().replace(/_/g, '-');
  if (!normalized) return null;
  if (LANGUAGE_ALIASES[normalized]) return LANGUAGE_ALIASES[normalized];

  const baseLanguage = normalized.split('-')[0];
  if (LANGUAGE_ALIASES[baseLanguage]) return LANGUAGE_ALIASES[baseLanguage];

  return LANGUAGE_CODE_PATTERN.test(baseLanguage) ? baseLanguage : null;
};

export const getActiveLanguageCodes = (languages = []) => new Set(
  (Array.isArray(languages) ? languages : [])
    .filter(language => language?.is_active !== 0 && language?.is_active !== false)
    .map(language => normalizeLanguageCode(language.language_code))
    .filter(Boolean)
);

export const isLanguageActive = (language, languages = []) => {
  const normalizedLanguage = normalizeLanguageCode(language);
  if (!normalizedLanguage) return false;
  return getActiveLanguageCodes(languages).has(normalizedLanguage);
};

export const readAuthLanguageOverride = () => {
  const rawValue = sessionStorage.getItem(AUTH_LANGUAGE_OVERRIDE_KEY);
  return {
    isPresent: rawValue !== null && rawValue !== '',
    rawValue,
    language: normalizeLanguageCode(rawValue)
  };
};

export const getAuthLanguageOverride = () => readAuthLanguageOverride().language;

export const resolveLegacyUserPreferenceLanguage = (serverLanguage) => (
  // During auth bootstrap, App and PreferenceContext can both touch the legacy
  // userPreferences cache. A pending auth-page override must win here until
  // PreferenceContext saves or rejects it, otherwise a slower /user/me response
  // can briefly restore the old server language into localStorage.
  getAuthLanguageOverride() || normalizeLanguageCode(serverLanguage) || 'en'
);

export const setAuthLanguageOverride = (language, activeLanguages = null) => {
  const normalizedLanguage = normalizeLanguageCode(language);
  if (!normalizedLanguage) return null;
  if (Array.isArray(activeLanguages) && !isLanguageActive(normalizedLanguage, activeLanguages)) {
    return null;
  }

  sessionStorage.setItem(AUTH_LANGUAGE_OVERRIDE_KEY, normalizedLanguage);
  localStorage.setItem(I18NEXT_LANGUAGE_KEY, normalizedLanguage);
  return normalizedLanguage;
};

export const clearAuthLanguageOverride = () => {
  sessionStorage.removeItem(AUTH_LANGUAGE_OVERRIDE_KEY);
};

export const clearInvalidAuthLanguageOverride = () => {
  const override = readAuthLanguageOverride();
  if (override.isPresent && !override.language) {
    clearAuthLanguageOverride();
    return true;
  }
  return false;
};

export const getCachedAuthenticatedLanguage = () => {
  const cachedPreferences = parseStorageJson('cachedPreferences');
  const cachedLanguage = normalizeLanguageCode(cachedPreferences?.userGlobal?.language);
  if (cachedLanguage) return cachedLanguage;

  const userPreferences = parseStorageJson('userPreferences');
  return normalizeLanguageCode(userPreferences?.language);
};

export const getStoredAnonymousLanguage = () => (
  normalizeLanguageCode(localStorage.getItem(I18NEXT_LANGUAGE_KEY))
);

export const getBrowserLanguage = () => {
  if (typeof navigator === 'undefined') return null;

  const browserLanguages = Array.isArray(navigator.languages) && navigator.languages.length > 0
    ? navigator.languages
    : [navigator.language];

  for (const browserLanguage of browserLanguages) {
    const normalizedLanguage = normalizeLanguageCode(browserLanguage);
    if (normalizedLanguage) return normalizedLanguage;
  }

  return null;
};

export const resolveInitialLanguage = () => {
  const overrideLanguage = getAuthLanguageOverride();
  if (overrideLanguage) return overrideLanguage;

  // Authenticated startup may use cached authenticated preferences for the first
  // paint. Unauthenticated auth pages intentionally ignore those caches so stale
  // profile language cannot masquerade as a new auth-page selection.
  if (localStorage.getItem('token')) {
    const cachedAuthenticatedLanguage = getCachedAuthenticatedLanguage();
    if (cachedAuthenticatedLanguage) return cachedAuthenticatedLanguage;
  }

  return getStoredAnonymousLanguage() || getBrowserLanguage() || 'en';
};
